// Third-party
import { FormatterCallbackFunction, AxisLabelsFormatterContextObject, XAxisPlotLinesOptions, XAxisPlotBandsOptions } from 'highcharts';

// Utils
import { TIME_PERIODS, ComparisonToolbarKeys, TIME_RANGE_PRESET } from '@/utils/constants';
import { PROPOSAL_ORIENTATION, PROPOSAL_ITEM_PURPOSE } from './utils/constants';

// Types
import AllocationDetails from '@/types/allocation-details';
import BMOGIFItem from '@/types/bmo-gif/bmo-gif-item';
import BMOGIFProjectionBasis from '@/types/bmo-gif/bmo-gif-projection-basis';
import BMOGIFScenario from '@/types/bmo-gif/bmo-gif-scenario';
import ComparisonContact from '@/types/comparison-contact';
import ComparisonItem from '@/types/comparison-item';
import ComparisonItemType from '@/types/comparison-item-type';
import ComparisonToolbarOptions from '@/types/comparison-toolbar-options';
import CorrelationMetric from '@/types/correlation-metric';
import Dictionary from '@/types/dictionary';
import GrowthTypes from '@/types/growth-types';
import IncomeStreamScenario from '@/types/income-stream-scenario';
import type { ComparisonTaxAnalysis } from '@/types/tax-analysis';
import Locale from '@/types/locale';
import LocalizedString from '@/types/localized-string';
import MockableUserInfos from '@/types/mockable-user-infos';
import OrientationPageFraction from '@/types/orientation-page-fraction';
import PERIOD_END_DATA_OPTIONS from '@/types/period-end-data-options';
import ProposalOrientation from '@/types/proposal-orientation';
import ProposalPaperFormat from '@/types/proposal-paper-format';
import ProposalPdfMetadata from '@/types/proposal-pdf-metadata';
import ProposalToolbarOption from '@/types/proposal-toolbar-option';
import QuestionnaireAnswers from '@/types/questionnaire-answers';
import RebalancedHoldingData from '@/types/rebalanced-holding-data';
import RiskPeriods from '@/types/risk-periods';
import RiskProfileItem from '@/types/risk-profile-item';
import TimePeriods from '@/types/time-periods';
import CommonInception from '@/types/common-inception';
import User from '@/types/user';
import YieldToMaturityInfo from '@/types/yield-to-maturity-info';
import { ORIENTATION } from '@/types/proposal';
import HistoricalScenario from '@/types/historical-scenario';
import CalculatedHistoricalScenario from '@/types/calculated-historical-scenario';
import AccountSet from '@/types/comparison-account-set';
import { FEATURE_FLAGS } from '@/enums/FeatureFlags';
import { CLIENT_LEVEL_VIEW } from '@/enums/ClientLevelView';

export type Proposal = {
  user: MockableUserInfos;
  appUser: User;
  items: ComparisonItem[];
  itemsCorrelation?: CorrelationMetric;
  template: ProposalTemplateV2;
  options: ProposalOptions;
  riskProfile?: RiskProfileItem;
  toolbarOptions: Dictionary<ProposalToolbarOption>;
  shortHistory?: boolean;
  hideBaseGifScenarios?: boolean;
  date?: Date;
  contact?: ProposalContact;
  comparisonToolbarOptions?: ComparisonToolbarOptions;
  showBaseCurrencies?: boolean;
  gifItems?: BMOGIFItem[];
  comment?: string;
  comparisonName?: string;
  readOnly?: boolean;
  itemIterators: Map<string, number>;
  selectedBenchmarkId?: string;
  customStrings: Dictionary<LocalizedString | string>;
  rationaleAnswers?: QuestionnaireAnswers;
  tableOfContents: TableOfContentsData[];
  commonInception?: CommonInception;
  selectedHistoricalScenarios?: HistoricalScenario[];
  calculatedHistoricalScenarios?: CalculatedHistoricalScenario[];
  monthEndDataOption?: PERIOD_END_DATA_OPTIONS;
  taxAnalysis?: ComparisonTaxAnalysis;
  accountSets?: AccountSet[];
};

export type ProposalTemplateOptions = {
  locale: Locale;
  hiddenLayouts: string[];
  divisions: string[];
  sections: string[];
  views: Dictionary<string>;
  theme: string;
  styleOptions: ProposalStyleOptionsV2;
  comments: Dictionary<LocalizedString>;
};

export interface ProposalItemAllocations {
  asOfDate?: Date;
  asset?: Dictionary<number | undefined>;
  detailedAsset?: AllocationDetails[];
  sector?: AllocationDetails[];
  country?: AllocationDetails[];
  creditQuality?: AllocationDetails[];
  maturity?: AllocationDetails[];
  fixedIncomeRegion?: AllocationDetails[];
  fixedIncomeSector?: AllocationDetails[];
  detailedHoldings?: AllocationDetails[];
}

export interface ProposalItemInvestmentInfo {
  modifiedDuration?: number;
  peRatio?: number;
  pbRatio?: number;
  distributionYield?: number;
  returnOnEquity?: number;
  capitalization?: AllocationDetails[];
  style?: AllocationDetails[];
  creditRatingClass?: AllocationDetails[];
  durationClass?: AllocationDetails[];
  yieldToMaturity?: YieldToMaturityInfo;
  averageCoupon?: number;
  averageQuality?: string;
  faceValue?: number;
  maturityDate?: Date;
  couponDates?: string;
  effectiveMaturity?: number;
  effectiveDuration?: number;
}

export type ProposalGIFData = {
  portfolio: {
    name: string;
    portfolioType?: string;
    classType?: string;
    planOption?: string;
    deathGuaranteeReset: boolean;
    term?: number;
    depositType: string;
    depositAmount?: number;
    purchaseDate: Date;
    contractMaturityDate?: Date;
    subsequentDepositAmount?: number;
    salesChargeType?: string;
    salesChargeAmount?: number;
    projectionBasis?: BMOGIFProjectionBasis;
    frequency: string;
    scheme: string;
    start?: number;
    end?: number;
    numYears?: number;
    holdings: {
      id: string;
      symbol: string;
      weight: number;
      name: string;
      type?: string;
    }[];
    averageMarketReturn: number;
    mer: number;
    equityPercent: number;
    deathBenefitFee: number;
    dob: Date;
    customRate?: number;
  };
  scenarios: BMOGIFScenario[];
  baseScenario: BMOGIFScenario;
};

export type ProposalContact = ComparisonContact;

export type ProposalIncomeStreamData = {
  defaultIncomeStream?: IncomeStreamScenario;
  incomeStreamDistribution?: IncomeStreamScenario[];
};

export type ProposalStyleBanners = {
  background: string;
  border?: string;
  color?: string;
  gradientRatio?: number;
  pattern?: string;
  patternColor?: string;
  coBrandingColors?: ProposalCoBrandingColors;
};

export type ProposalCoBrandingColors = {
  primary?: string;
};

export type ProposalStyleOptionsV2 = {
  banners: ProposalStyleBanners;
  orientation: ProposalOrientation;
  paperFormat?: ProposalPaperFormat;
  includeFundProfiles?: boolean;
  includePortfolioProfiles?: boolean;
  includeGroupProfiles?: boolean;
};

export type ProposalOptions = {
  locale: Locale;
  theme: string;
  comments: Dictionary<LocalizedString>;
  includeFundProfiles: boolean;
  includePortfolioProfiles: boolean;
  includeGroupProfiles: boolean;
  orientation: ProposalOrientation;
};

export enum ProposalBuilderVersion {
  V1 = 'v1',
  V2 = 'v2',
}

export type BaseProposalTemplate = {
  key: string;
  builderVersion: ProposalBuilderVersion;
  title: LocalizedString;
  description?: LocalizedString;
  previewLogoUrl?: LocalizedString;
};

export type ProposalTemplateV1 = BaseProposalTemplate & {
  builderVersion: ProposalBuilderVersion.V1;
  footer?: (props: any) => JSX.Element;
  form?: (props: any) => JSX.Element;
  header?: (props: any) => JSX.Element;
  pageStart?: number;
  orientation?: PROPOSAL_ORIENTATION.PORTRAIT | PROPOSAL_ORIENTATION.LANDSCAPE;
};

export type ProposalTemplate = ProposalTemplateV1 | ProposalTemplateV2;

export enum ProposalDownloadFormKeys {
  AdvisorBranding = 'ADVISOR_BRANDING',
}

export type ProposalDownloadFormBase = {
  key: ProposalDownloadFormKeys;
};

export type ProposalAdvisorBrandingForm = ProposalDownloadFormBase & {
  key: ProposalDownloadFormKeys.AdvisorBranding;
  config: {
    fields?: string[];
    showBeforeDownload?: boolean;
    preFilledUsersInfo?: boolean;
    showSectionHeader?: boolean;
  };
};

export type ProposalDownloadForm = ProposalAdvisorBrandingForm;

export type ProposalDownloadFormValues = {
  headerInfo: MockableUserInfos;
  defaultValues?: MockableUserInfos;
  contact?: string;
  overrideMock: boolean;
};

export type ProposalTemplateV2 = BaseProposalTemplate & {
  builderVersion: ProposalBuilderVersion.V2;
  styleUrl?: string;
  root: ProposalTemplateDivisionElement[];
  themes: ProposalTheme[];
  options: ProposalTemplateOptions;
  sidebarTabs: ProposalSidebarTab[];
  name?: LocalizedString;
  websiteUrl?: string;
  downloadForm?: ProposalDownloadForm;
  requirements: ProposalTemplateRequirements;
  itemManagement: ProposalTemplateItemManagement;
  customParameters?: ProposalTemplateCustomParameters;
  availableOrientations: ProposalAvailableOrientation[];
  customDisclaimer?: ProposalCustomDisclaimer;
  customHeader?: ProposalHeaderConfig;
  customFooter?: ProposalFooterConfig;
  linkedSections?: ProposalLinkedSections[];
  pageFormat?: ProposalPageFormat;
  benchmarksBreakdownDisclaimers?: LocalizedString;
  proposalPdfMetadata?: ProposalPdfMetadata;
};

export type ProposalPageFormat = {
  width: number;
  height: number;
};

export enum ProposalSectionLinkType {
  SAME = 'SAME', // All sections will be on if anyone one in the list is turned on. Same when turning one off.
  OPPOSITE = 'OPPOSITE', // All other sections will be off if one in the list is turned on. All section of off when one is turned off.
  OPPOSITE_MANDATORY = 'OPPOSITE_MANDATORY', // All other sections will be off if one in the list is turned on.
  // All other sections will be on if the currently selected one is turned off.
}

export type ProposalLinkedSections = {
  sections: string[];
  type: ProposalSectionLinkType;
};

export type ProposalCustomDisclaimer = {
  header: LocalizedString;
  footer: LocalizedString;
};

export type ProposalHeaderConfig = {
  showComparisonName?: boolean;
  content?: ProposalTemplateDataElement;
};

export type ProposalFooterConfig = {
  content?: ProposalTemplateDataElement;
  extraDisclaimers?: LocalizedString[];
};

export type ProposalTemplateCustomParameters = {
  // this was introduced for MD. If there only is 1 item in the comparison, we should threat it as item 2 (recommended),
  // so we 'shift' item references by one.
  singleItemShift?: boolean;
};

export enum TemplateRequirementKeys {
  MinGIFItems = 'MIN_GIF_ITEMS',
  RiskProfile = 'RISK_PROFILE',
  MinItems = 'MIN_ITEMS',
  MinGIFFunds = 'MIN_GIF_FUNDS',
  MinGIFBenchmarks = 'MIN_GIF_BENCHMARKS',
  MaxItemPurpose = 'MAX_ITEM_PURPOSE',
  ComparisonPortfolioRiskProfile = 'COMPARISON_PORTFOLIO_RISK_PROFILE',
}

export type ProposalTemplateRequirement =
  | ProposalTemplateRiskProfileRequirement
  | ProposalTemplateMinGifItemsRequirement
  | ProposalTemplateMinItemsRequirement
  | ProposalTemplateMinGifFundsRequirement
  | ProposalTemplateMinGifBenchmarksRequirement
  | ProposalTemplateMaxItemPurposeRequirement
  | ProposalTemplateComparisonPortfolioRiskProfileRequirement;

export type ProposalTemplateRiskProfileRequirement = {
  key: TemplateRequirementKeys.RiskProfile;
  value: boolean;
};

export type ProposalTemplateComparisonPortfolioRiskProfileRequirement = {
  key: TemplateRequirementKeys.ComparisonPortfolioRiskProfile;
  value: boolean;
};

export type ProposalTemplateMinGifItemsRequirement = {
  key: TemplateRequirementKeys.MinGIFItems;
  value: number;
};

export type ProposalTemplateMinItemsRequirement = {
  key: TemplateRequirementKeys.MinItems;
  type: ProposalItemType;
  value: number;
};

export type ProposalTemplateMinGifFundsRequirement = {
  key: TemplateRequirementKeys.MinGIFFunds;
  value: number;
};

export type ProposalTemplateMinGifBenchmarksRequirement = {
  key: TemplateRequirementKeys.MinGIFBenchmarks;
  value: number;
  minRate?: number;
  maxRate?: number;
};

export type ProposalTemplateMaxItemPurposeRequirement = {
  key: TemplateRequirementKeys.MaxItemPurpose;
  type: PROPOSAL_ITEM_PURPOSE;
  max: number;
};

export type ProposalTemplateRequirements = ProposalTemplateRequirement[];

export type FormattedProposalTemplateRequirement = {
  key: string;
  text: string;
  actionText?: string;
  callback?: () => void;
};

export type FormattedProposalTemplateRequirements = FormattedProposalTemplateRequirement[];

export type ProposalAvailableOrientation = {
  orientation: ProposalOrientation;
  maxItemsSelected: number;
};

export enum ProposalItemType {
  Alt = 'alt',
  Fund = 'fund',
  Stock = 'stock',
  Group = 'group',
  Benchmark = 'benchmark',
  Portfolio = 'portfolio',
  BmoGIF = 'bmo-gif',
}

export type ProposalAllowedItemType = 'alt' | 'fund' | 'stock' | 'benchmark' | 'portfolio' | 'bmo-gif' | 'group';

export type ProposalTemplateItemManagement = {
  allowedItemTypes: ProposalAllowedItemType[];
};

export enum LayoutType {
  Grid = 'GRID',
  Switch = 'SWITCH',
  Card = 'CARD',
}

export enum DAOType {
  AssetAllocation = 'ASSET_ALLOCATION',
  CountryAllocation = 'COUNTRY_ALLOCATION',
  CalendarYearReturns = 'CALENDAR_YEAR_RETURNS',
  HypotheticalReturn = 'HYPOTHETICAL_RETURN',
  TrailingReturns = 'TRAILING_RETURNS',
  Overview = 'OVERVIEW',
  RiskReward = 'RISK_REWARD',
  IndustryHoldings = 'INDUSTRY_HOLDINGS',
  CorrelationMatrix = 'CORRELATION_MATRIX',
  ComparisonCorrelationMatrix = 'COMPARISON_CORRELATION_MATRIX',
  DrawdownAnalysis = 'DRAWDOWN_ANALYSIS',
  Toolbar = 'TOOLBAR',
  Text = 'TEXT',
  Glossary = 'GLOSSARY',
  FixedIncomeMaturity = 'FIXED_INCOME_MATURITY',
  FixedIncomeStyle = 'FIXED_INCOME_STYLE',
  FixedIncomeSectorAllocation = 'FIXED_INCOME_SECTOR_ALLOCATION',
  FixedIncomeRegionAllocation = 'FIXED_INCOME_REGION_ALLOCATION',
  CreditQuality = 'CREDIT_QUALITY',
  FixedIncomeMetrics = 'FIXED_INCOME_METRICS',
  EquityRegionAllocation = 'EQUITY_REGION_ALLOCATION',
  EquityStyleAllocation = 'EQUITY_STYLE_ALLOCATION',
  SectorAllocation = 'SECTOR_ALLOCATION',
  PortfolioHoldings = 'PORTFOLIO_HOLDINGS',
  PortfolioFundHoldings = 'PORTFOLIO_FUND_HOLDINGS',
  PortfolioYield = 'PORTFOLIO_YIELD',
  EquityCountryAllocation = 'EQUITY_COUNTRY_ALLOCATION',
  UpDown = 'UP_DOWN',
  BestWorst = 'BEST_WORST',
  RiskMetrics = 'RISK_METRICS',
  DetailedHoldings = 'DETAILED_HOLDINGS',
  CoreEquityMetrics = 'CORE_EQUITY_METRICS',
  LipperLeaderScores = 'LIPPER_LEADER_SCORES',
  IncomeStreamGraph = 'INCOME_STREAM_GRAPH',
  IncomeStreamSummary = 'INCOME_STREAM_SUMMARY',
  IncomeStream = 'INCOME_STREAM',
  Commentary = 'COMMENTARY',
  HTMLContent = 'HTML_CONTENT',
  DrawdownDetails = 'DRAWDOWN_DETAILS',
  BmoGifSummary = 'BMO_GIF_SUMMARY',
  BmoGif = 'BMO_GIF',
  NoOp = 'NO_OP',
  Disclaimers = 'DISCLAIMERS',
  RiskProfile = 'RISK_PROFILE',
  GenericAllocations = 'GENERIC_ALLOCATIONS',
  RebalanceAnalysis = 'REBALANCE_ANALYSIS',
  AllocationAnalysis = 'ALLOCATION_ANALYSIS',
  TableOfContents = 'TABLE_OF_CONTENTS',
  ESGOverview = 'ESG_OVERVIEW',
  ESGCarbonIntensity = 'ESG_CARBON_INTENSITY',
  ESGImpliedTemperatureRise = 'ESG_IMPLIED_TEMPERATURE_RISE',
  PlanningSummary = 'PLANNING_SUMMARY',
  PlanningAnnualizedRoi = 'PLANNING_ANNUALIZED_ROI',
  PlanningAssetAllocation = 'PLANNING_ASSET_ALLOCATION',
  AltsMetrics = 'ALTS_METRICS',
  HistoricalScenariosGraph = 'HISTORICAL_SCENARIOS_GRAPH',
  HistoricalScenariosMetrics = 'HISTORICAL_SCENARIOS_METRICS',
  TaxAnalysisOverview = 'TAX_ANALYSIS_OVERVIEW',
  TaxAnalysisTransitionImplications = 'TAX_ANALYSIS_TRANSITION_IMPLICATIONS',
  TaxAnalysisPortfolioChangeLog = 'TAX_ANALYSIS_PORTFOLIO_CHANGE_LOG',
  ClientSummary = 'CLIENT_SUMMARY',
  PortfolioValueBreakdown = 'PORTFOLIO_VALUE_BREAKDOWN',
  AggregatedAssetAllocation = 'AGGREGATED_ASSET_ALLOCATION',
  singlePageFund = 'SINGLE_PAGE_FUND',
  singlePagePortfolio = 'SINGLE_PAGE_PORTFOLIO',
  singlePageGroup = 'SINGLE_PAGE_GROUP',
}

export enum DAOGroupByTotalPosition {
  TOP = 'TOP',
  BOTTOM = 'BOTTOM',
  NONE = 'NONE',
}

export enum ProposalTemplateElementType {
  Data = 'DATA',
  Layout = 'LAYOUT',
  Section = 'SECTION',
  Division = 'DIVISION',
}

export enum ProposalItemSelectionType {
  ITEM_SELECTOR = 'ITEM_SELECTOR',
  ITEM_LISTER = 'ITEM_LISTER',
}

export enum ProposalItemSelectionFilterType {
  OrCondition = 'OR_CONDITION',
  ItemType = 'ITEM_TYPE',
  GIC = 'GIC',
  Universe = 'UNIVERSE',
  Manufacturer = 'MANUFACTURER',
}

export enum ProposalItemSelectionUniverse {
  InsuranceFunds = 'INSURANCE_FUNDS',
}

export enum SidebarTabs {
  OPTIONS = 'Options',
  CUSTOMIZE = 'Customize',
}

export type ProposalItemSelectionItemTypeFilter = {
  type: ProposalItemSelectionFilterType.ItemType;
  value: ProposalItemType;
};

export type ProposalItemSelectionOrConditionFilter = {
  type: ProposalItemSelectionFilterType.OrCondition;
  conditions: ProposalItemSelectionFilter[];
};

export type ProposalItemSelectionManufacturerFilter = {
  type: ProposalItemSelectionFilterType.Manufacturer;
  value: string;
};

export type ProposalItemSelectionUniverseFilter = {
  type: ProposalItemSelectionFilterType.Universe;
  value: ProposalItemSelectionUniverse;
};

export type ProposalItemSelectionGICFilter = {
  type: ProposalItemSelectionFilterType.GIC;
  minRate?: number;
  maxRate?: number;
};

export type ProposalItemSelectionFilter =
  | ProposalItemSelectionOrConditionFilter
  | ProposalItemSelectionItemTypeFilter
  | ProposalItemSelectionGICFilter
  | ProposalItemSelectionManufacturerFilter
  | ProposalItemSelectionUniverseFilter;

export type ProposalItemSelectionOption = {
  label: LocalizedString;
  filters?: ProposalItemSelectionFilter[];
};

export type ProposalSidebarElementBase = {
  type: string;
  permissions?: string[];
  borders?: ('BOTTOM' | 'TOP')[];
};

export type ProposalItemSelection = ProposalSidebarElementBase & {
  itemSelectors: ProposalItemSelectionOption[];
  itemNameOverride?: LocalizedString[];
  itemColorOverride?: string[];
};

export type ProposalSidebarTemplate = ProposalSidebarElementBase & {
  type: 'TEMPLATE';
};

export type EditProposalAdvisorInfo = ProposalSidebarElementBase & {
  type: 'EDIT_PROPOSAL_ADVISOR_INFO';
};

export type ProposalSidebarItemSelector = ProposalItemSelection & {
  type: 'ITEM_SELECTOR';
};

export type ProposalSidebarItemLister = ProposalItemSelection & {
  type: 'ITEM_LISTER';
  showIncludeFundProfiles?: boolean;
  showIncludePortfolioProfiles?: boolean;
  showIncludeGroupProfiles?: boolean;
  showShortHistory?: boolean;
  showProposalItemPurposeSelector?: boolean;
};

export type ProposalSidebarLanguage = ProposalSidebarElementBase & {
  type: 'LANGUAGE';
};

export type ProposalSidebarProposalSections = ProposalSidebarElementBase & {
  type: 'PROPOSAL_SECTIONS';
};

export type ProposalSidebarPatternSelector = ProposalSidebarElementBase & {
  type: 'PATTERN_SELECTOR';
};

export type ProposalSidebarColorSelector = ProposalSidebarElementBase & {
  type: 'COLOR_SELECTOR';
};

export type ProposalSidebarCoBrandingColor = ProposalSidebarElementBase & {
  type: 'COBRANDING_COLOR';
};

export type ProposalSidebarOrientation = ProposalSidebarElementBase & {
  type: 'ORIENTATION';
};

export type ProposalSidebarPaperFormat = ProposalSidebarElementBase & {
  type: 'PAPER_FORMAT';
};

export type ProposalSidebarElement =
  | ProposalSidebarTemplate
  | EditProposalAdvisorInfo
  | ProposalSidebarItemSelector
  | ProposalSidebarItemLister
  | ProposalSidebarLanguage
  | ProposalSidebarProposalSections
  | ProposalSidebarPatternSelector
  | ProposalSidebarColorSelector
  | ProposalSidebarOrientation
  | ProposalSidebarPaperFormat
  | ProposalSidebarCoBrandingColor;

export type ProposalSidebarTab = {
  key: string;
  label: LocalizedString;
  elements: ProposalSidebarElement[];
};

// ------------------------------ Proposal Template Element Types ------------------------------------------------------
// these are config types, used for loading / saving / validating templates

export enum ProposalSidebarControl {
  None = 'NONE',
  Radio = 'RADIO',
  Toggle = 'TOGGLE',
  Checkbox = 'CHECKBOX',
  Hide = 'HIDE',
}

export type ProposalHideableLayout = {
  label: LocalizedString;
  key: string;
  pageFraction: OrientationPageFraction;
  disableRules?: ElementDisableRule[];
};

export enum ElementBuildRuleTypes {
  Item = 'ITEM',
  ItemType = 'ITEM_TYPE',
  ItemProperty = 'ITEM_PROPERTY',
  ProposalProperty = 'PROPOSAL_PROPERTY',
  RiskProfile = 'RISK_PROFILE',
  AssetAllocation = 'ASSET_ALLOCATION',
  Permission = 'PERMISSION',
  PageOverflowIndex = 'PAGE_OVERFLOW_INDEX',
  IncludeProfiles = 'INCLUDE_PROFILES',
  Orientation = 'ORIENTATION',
  GIFItems = 'GIF_ITEMS',
  ItemPurpose = 'ITEM_PURPOSE',
  FeatureFlag = 'FEATURE_FLAG',
  AccountSetPortfolios = 'ACCOUNT_SET_PORTFOLIOS',
}

export enum ItemProfileType {
  Fund = 'FUND',
  Portfolio = 'PORTFOLIO',
  Group = 'GROUP',
}

export enum ElementDisableRuleTypes {
  DependentSection = 'DEPENDENT_SECTION',
  SelectedSection = 'SELECTED_SECTION',
  RiskProfile = 'RISK_PROFILE',
  MinItems = 'MIN_ITEMS',
  ItemPurposeCurrentRecommended = 'ITEM_PURPOSE_CURRENT_RECOMMENDED',
  ItemPurposeItemType = 'ITEM_PURPOSE_ITEM_TYPE',
  ItemProperty = 'ITEM_PROPERTY',
}

export type RiskProfileRule = {
  type: ElementBuildRuleTypes.RiskProfile;
  useComparisonRiskProfile?: boolean;
  usePortfolioRiskProfile?: boolean;
};

export type AssetAllocationRule = {
  type: ElementBuildRuleTypes.AssetAllocation;
  minAllocation?: number;
  indexes?: number[];
  allocationType: 'EQUITY' | 'FIXED_INCOME' | 'OTHER';
};

export type PermissionRuleOperator = 'AND' | 'OR' | 'NOT';

export type PermissionRule = {
  type: ElementBuildRuleTypes.Permission;
  permissions: string[];
  operator?: PermissionRuleOperator; // defaults to 'AND'
};

export type FeatureFlagRule = {
  type: ElementBuildRuleTypes.FeatureFlag;
  flags: FEATURE_FLAGS[];
  operator?: 'AND' | 'OR'; // defaults to 'AND'
};

export type ItemRule = {
  type: ElementBuildRuleTypes.Item;
  indexes?: number[];
};

export type ItemTypeRule = {
  type: ElementBuildRuleTypes.ItemType;
  itemTypes: ComparisonItemType[];
};

export type ItemPropertyRule = {
  type: ElementBuildRuleTypes.ItemProperty;
  property: string;
};

export type ProposalPropertyRule = {
  type: ElementBuildRuleTypes.ProposalProperty;
  property: string;
};

export type PageOverflowIndexRule = {
  type: ElementBuildRuleTypes.PageOverflowIndex;
  firstPageOnly: boolean;
};

export type IncludeProfileRule = {
  type: ElementBuildRuleTypes.IncludeProfiles;
  profileType: ItemProfileType;
};

export type OrientationRule = {
  type: ElementBuildRuleTypes.Orientation;
  orientation: PROPOSAL_ORIENTATION;
};

export type ItemPurposeRule = {
  type: ElementBuildRuleTypes.ItemPurpose;
  purpose: PROPOSAL_ITEM_PURPOSE;
};

export type AccountSetPortfoliosRule = {
  type: ElementBuildRuleTypes.AccountSetPortfolios;
  view: CLIENT_LEVEL_VIEW;
};

export type GIFItemsRule = {
  type: ElementBuildRuleTypes.GIFItems;
};

export type ProposalTemplateElement =
  | ProposalTemplateDivisionElement
  | ProposalTemplateSectionElement
  | ProposalTemplateLayoutElement
  | ProposalTemplateDataElement;

export type NullProposalLayoutElement = ProposalTemplateLayoutElement;

export type ProposalTemplateItemIterator = {
  key: string;
  maxItemPerPage?: number;
  partialGrid?: boolean;
  padStart?: number;
  padEnd?: number;
  conditions?: ProposalItemIteratorCondition[];
};

export enum ProposalItemIteratorConditionType {
  ItemType = 'ITEM_TYPE',
  ItemProperty = 'ITEM_PROPERTY',
  ItemRiskProfile = 'ITEM_RISK_PROFILE',
  ItemPurpose = 'ITEM_PURPOSE',
}

export type ProposalItemIteratorCondition =
  | IteratorItemTypeCondition
  | IteratorItemRiskProfileCondition
  | IteratorItemPurposeCondition
  | IteratorItemPropertyCondition;

export type IteratorItemTypeCondition = {
  type: ProposalItemIteratorConditionType.ItemType;
  value: ProposalAllowedItemType[];
};

export type IteratorItemRiskProfileCondition = {
  type: ProposalItemIteratorConditionType.ItemRiskProfile;
  value: boolean;
};

export type IteratorItemPurposeCondition = {
  type: ProposalItemIteratorConditionType.ItemPurpose;
  value: PROPOSAL_ITEM_PURPOSE;
};

export type IteratorItemPropertyCondition = {
  type: ProposalItemIteratorConditionType.ItemProperty;
  property: string;
};

export type ProposalTemplateLayoutElement = {
  type: ProposalTemplateElementType.Layout;
  layout: {
    type: LayoutType;
    rows?: number;
    columns?: number;
    autoCol?: boolean;
    className?: string;
    key?: string;
    itemIterator?: ProposalTemplateItemIterator;
  };
  elements: ProposalTemplateElement[];
  settings?: ProposalTemplateElementSettings;
  buildRules?: ElementBuildRule[];
};

export type ProposalTemplateDataElement = {
  type: ProposalTemplateElementType.Data;
  dataView: DataViewOptions;
  DAO: DAOConfigs;
  settings?: ProposalTemplateElementSettings;
  toolbarOptionConfig?: ToolbarOptionConfig;
  buildRules?: ElementBuildRule[];
  textAreaOptionConfig?: TextAreaOptionConfig;
};

export type ToolbarOptionConfig = {
  key: string;
  comparisonToolbarKey?: ComparisonToolbarKeys;
  toolbarElements?: ToolbarElement[];
  toolbarElementTitles?: Record<ToolbarElement, LocalizedString>;
  dateQuickRangeValues?: TIME_PERIODS[];
  dateQuickRangePreset?: TIME_RANGE_PRESET;
  defaultValues?: ToolbarOptionDefaultValues;
  disableRebalancePeriod?: boolean;
  className?: string; // use this when setting a class on a toolbar directly on a section. You should not have to do this really, but you can :)
  hidePeriodLabel?: boolean;
  decimalPlace?: number;
};

export type ToolbarOptionDefaultValues = {
  initialCapital?: number;
  timePeriod?: 'YT' | '1Y' | '3Y' | '5Y' | '10Y' | 'INC' | 'CI';
  rebalancePeriod?: 'YEARLY' | 'SEMIANNUALLY' | 'QUARTERLY' | 'DAILY';
  useMonthly?: boolean;
};

export type TextAreaOptionConfig = {
  key: string;
  placeholder?: LocalizedString;
  maxChars?: number;
  default?: LocalizedString;
};

export type ElementBuildRule =
  | RiskProfileRule
  | ItemRule
  | ItemTypeRule
  | ItemPropertyRule
  | ProposalPropertyRule
  | AssetAllocationRule
  | PermissionRule
  | FeatureFlagRule
  | PageOverflowIndexRule
  | IncludeProfileRule
  | OrientationRule
  | GIFItemsRule
  | ItemPurposeRule
  | AccountSetPortfoliosRule;

export type Tooltipable = {
  sidebarTooltip?: SidebarTooltip;
};

export type SidebarTooltip = {
  title?: LocalizedString;
  message?: LocalizedString;
};

export type DependentSectionRule = {
  type: ElementDisableRuleTypes.DependentSection;
  sections: string[];
  operator: 'AND' | 'OR';
} & Tooltipable;

export type SelectedSectionRule = {
  type: ElementDisableRuleTypes.SelectedSection;
  sections: string[];
  operator: 'AND' | 'OR';
} & Tooltipable;

export type RiskProfileDisableRule = {
  type: ElementDisableRuleTypes.RiskProfile;
  requireAnswers?: boolean;
} & Tooltipable;

export type MinItemsSectionRule = {
  type: ElementDisableRuleTypes.MinItems;
  itemType: ComparisonItemType;
  value: number;
} & Tooltipable;

export type ItemPurposeCurrentRecommendedSectionRule = {
  type: ElementDisableRuleTypes.ItemPurposeCurrentRecommended;
} & Tooltipable;

export type ItemPurposeItemTypeSectionRule = {
  type: ElementDisableRuleTypes.ItemPurposeItemType;
  itemTypes: ComparisonItemType[];
} & Tooltipable;

export type ElementItemPropertyRule = {
  type: ElementDisableRuleTypes.ItemProperty;
  property: string;
} & Tooltipable;

export type ProposalTemplateElementSettings = {
  colors?: Dictionary<Dictionary<string>>;
};

export type ElementDisableRule =
  | DependentSectionRule
  | SelectedSectionRule
  | RiskProfileDisableRule
  | MinItemsSectionRule
  | ItemPurposeCurrentRecommendedSectionRule
  | ItemPurposeItemTypeSectionRule
  | ElementItemPropertyRule;

export type ProposalTemplateSectionElement = {
  type: ProposalTemplateElementType.Section;
  layout: ProposalTemplateLayoutElement;
  key: string;
  label: LocalizedString;
  subtitle?: LocalizedString;
  pageFraction: OrientationPageFraction;
  tableOfContentsGroup?: TableOfContentsGroupData;
  showTitle?: boolean;
  showHeader?: boolean;
  showFooter?: boolean;
  showTitleOnPageOverflow?: boolean;
  showItemNamesInDisclaimers?: boolean;
  showDetailedDatesInDisclaimer?: boolean;
  hideSpecificDisclaimerKeys?: string[];
  sidebarControl: ProposalSidebarControl;
  hideableLayouts?: ProposalHideableLayout[];
  sidebarTooltip?: SidebarTooltip;
  linkedSectionKeys?: Array<string>;
  settings?: ProposalTemplateElementSettings;
  buildRules?: ElementBuildRule[];
  disableRules?: ElementDisableRule[];
  forced?: boolean; // used to bypass the test of the selected sections.
  toolbar?: ToolbarOptionConfig;
  itemIterator?: ProposalTemplateItemIterator;
  comparisonIterator?: ComparisonIterator;
  allowedItemTypes?: ProposalAllowedItemType[];
  sectionType?: ProposalSectionType;
  accessibilityDescription?: LocalizedString;
  extraDisclaimers?: CustomDisclaimerConfig[];
};

export type ComparisonIterator = {
  propertyToIterate: string;
};

export type ProposalTemplateDivisionElement = {
  type: ProposalTemplateElementType.Division;
  key: string;
  forced?: boolean;
  label: LocalizedString;
  sidebarControl: ProposalSidebarControl;
  sections: ProposalTemplateSectionElement[];
  showTitle?: boolean;
  showDivider?: boolean;
  settings?: ProposalTemplateElementSettings;
  buildRules?: ElementBuildRule[];
  disableRules?: ElementDisableRule[];
};

export enum ProposalSectionType {
  BmoGifSummary = 'BMO_GIF_SUMMARY',
  Cover = 'COVER',
  CustomRiskProfile = 'CUSTOM_RISK_PROFILE',
  CustomTop = 'CUSTOM_TOP',
  Disclaimer = 'DISCLAIMER',
  InstrumentCustomDisclaimers = 'INSTRUMENT_CUSTOM_DISCLAIMERS',
  Marketing = 'MARKETING',
  MarketingPageSort = 'MARKETING_PAGE_SORT',
  Questionnaire = 'QUESTIONNAIRE',
}

export enum ValueFormatter {
  DEFAULT = 'DEFAULT',
  PERCENTAGE = 'PERCENTAGE',
}

// Section Options

// DAO OPTIONS

export type FilterItemsByIndexDAOOptions = {
  selection?: number[];
  itemTypes?: [ProposalItemType];
  itemIterator?: string;
};

export type GroupByAllocationsDAOOptions = {
  totalPosition?: DAOGroupByTotalPosition;
  showGroups?: boolean;
  showElements?: boolean;
  showTotals?: boolean;
  showGrandTotal?: boolean;
  order?: {
    groupKey: string | null;
    keys: string[];
    labelKey?: string;
  }[];
};

export type GroupByESGOverviewDAOOptions = {
  totalPosition?: DAOGroupByTotalPosition;
  order?: {
    groupKey: string | null;
    keys: string[];
  }[];
};

export type ExtraColumnsDAOOptions = {
  columns: string[];
};

export type GroupByPortfolioBreakdownsDAOOptions = {};

export type FilterTopXDAOOptions = {
  count: number;
  start?: number;
  omit?: string[];
};

export type TableOrientationDAOOptions = {
  orientation: ORIENTATION;
};

export type ContentCellDAOOptions = {
  cellType: any;
};

export type CustomColumnNamesDAOOptions = {
  columns: Array<{
    name: LocalizedString;
    className?: string;
    color?: string;
  }>;
};

export type CustomFieldNamesDAOOptions = {
  names: LocalizedString[];
};

export enum CustomDisclaimerType {
  ADD = 'ADD',
  REPLACE = 'REPLACE',
  REMOVE = 'REMOVE',
  CHANGE_KEY = 'CHANGE_KEY',
}

export type CustomDisclaimerConfig = {
  type: CustomDisclaimerType;
  key?: string;
  newKey?: string;
  customString?: LocalizedString;
  isHTML?: boolean;
  useDates?: boolean;
  useDetailedDates?: boolean;
  usePerformanceDates?: boolean;
  useRiskDates?: boolean;
  useRollingDates?: boolean;
  doNotMergeOnPage?: boolean;
  datesItemPrefix?: string;
};

export type CustomDisclaimerDAOOptions = {
  configs: CustomDisclaimerConfig[];
};

export type LocalizedTextDAOOptions = {
  texts: Array<{ text: LocalizedString; className?: string; tag?: string }>;
  pageOverflowSuffix?: { text: LocalizedString; className?: string };
};

export type RegionExclusionConfigDAOOptions = {
  countryExclusions: Dictionary<string[]>;
  regionExclusions: string[];
};

export type CountryInclusionConfigDAOOptions = {
  countryInclusions: string[];
};

export type ExcludeUniversesDAOOptions = {
  universes: string[];
};

export type GlossaryContent = {
  key?: string;
  tag: string;
  link?: string;
  children?: GlossaryContent[];
};

export type GlossarySection = {
  key: string;
  showKey?: boolean;
  tag: string;
  content: GlossaryContent[];
};

export type GlossaryConfigDAOOptions = {
  sections: GlossarySection[];
  definitionPerPage: Partial<Record<Locale, number>>;
};

export type HtmlContentElement = {
  content: LocalizedString;
  className?: string;
  clamp?: number;
  tag?: string;
};

export type ConditionalHtmlContent = {
  property: string;
  values: { values: any[]; html: HtmlContentElement }[];
  default?: HtmlContentElement;
};

export type HTMLContentDAOOptions = {
  html?: HtmlContentElement[];
  conditional?: ConditionalHtmlContent;
  textRegex?: TextRegex;
};

export type CommentaryDAOOptions = {
  key: string;
};

export type ProposalText = {
  _id?: string;
  text: string;
  className?: string;
  tag?: string;
  clamp?: number;
};

export type DAOGetTextsMetadata = {
  pageOverflowIndex?: number;
  pageNumber?: string | number;
  pageNumberPadded2?: string | number;
  numberOfPages?: number;
  itemIndex?: number;
  textRegex?: TextRegex;
  sectionIteratorIndex?: number;
};

export type TextRegex = {
  type: TextRegexType;
  regexp: string;
  newSubStr: string;
};

export enum TextRegexType {
  REPLACE = 'REPLACE',
}

export enum AreaFillConditionType {
  ItemType = 'ITEM_TYPE',
}

export type AreaFillConditionBase = {
  type: AreaFillConditionType;
};

export type AreaFillItemBasedCondition = AreaFillConditionBase & {
  value: ComparisonItemType[];
};

export type AreaFillConditions = AreaFillItemBasedCondition;

// DAO CONFIGS

export type DAOConfigs =
  | ToolbarConfig
  | OverviewConfig
  | AssetAllocationConfig
  | AggregatedAssetAllocationConfig
  | CountryAllocationConfig
  | CalendarYearReturnsConfig
  | IndustryHoldingsConfig
  | TextConfig
  | GlossaryConfig
  | HTMLContentConfig
  | CommentaryConfig
  | FixedIncomeRegionAllocationConfig
  | FixedIncomeSectorAllocationConfig
  | EquityRegionAllocationConfig
  | SectorAllocationConfig
  | EquityCountryAllocationConfig
  | CreditQualityConfig
  | UpDownConfig
  | BestWorstConfig
  | HypotheticalReturnConfig
  | RiskMetricsConfig
  | DetailedHoldingsConfig
  | PortfolioHoldingsConfig
  | PortfolioFundHoldingsConfig
  | FixedIncomeMetricsConfig
  | FixedIncomeMaturityConfig
  | LipperLeaderScoresConfig
  | DrawdownAnalysisConfig
  | RiskRewardConfig
  | FixedIncomeStyleConfig
  | CorrelationMatrixConfig
  | CoreEquityMetricsConfig
  | EquityStyleAllocationConfig
  | DrawdownDetailsConfig
  | BmoGifSummaryConfig
  | BmoGifConfig
  | IncomeStreamGraphConfig
  | IncomeStreamSummaryConfig
  | IncomeStreamConfig
  | RiskProfileConfig
  | GenericAllocationsConfig
  | RebalanceAnalysisConfig
  | TaxAnalysisOverviewConfig
  | TaxAnalysisTransitionImplicationsConfig
  | TaxAnalysisPortfolioChangeLogConfig
  | AllocationAnalysis
  | ESGOverviewConfig
  | ESGCarbonIntensityConfig
  | ESGImpliedTemperatureRiseConfig
  | PortfolioValueBreakdownConfig
  | ClientSummaryConfig;

export type BaseDAOConfig = {
  filterItemsByIndexDAOOptions?: FilterItemsByIndexDAOOptions;
  tableOrientationDAOOptions?: TableOrientationDAOOptions;
  contentCellDAOOptions?: ContentCellDAOOptions;
  customColumnNamesDAOOptions?: CustomColumnNamesDAOOptions;
  customFieldNamesDAOOptions?: CustomFieldNamesDAOOptions;
  customDisclaimerDAOOptions?: CustomDisclaimerDAOOptions;
  excludeUniversesDAOOptions?: ExcludeUniversesDAOOptions;
};

export type ToolbarConfig = BaseDAOConfig & {
  type: DAOType.Toolbar;
};

export type OverviewConfig = BaseDAOConfig & {
  type: DAOType.Overview;
  showSize?: boolean;
  showBeta?: boolean;
  showTotalCost?: boolean;
  showMER?: boolean;
  showMERBreakdown?: boolean;
  showMERAsTotalCost?: boolean;
  showMinInvestment?: boolean;
  showCommonInception?: boolean;
  showTax?: boolean;
  showLipperScores?: boolean;
  growthPeriods?: GrowthTypes[];
  stdDevPeriods?: RiskPeriods[];
  customTypes?: Dictionary<LocalizedString>;
  forcedInceptionDateOnBenchmarks?: number;
  showInceptionDate?: boolean;
};

export type GenericAllocationsConfig = BaseDAOConfig & {
  type: DAOType.GenericAllocations;
  allocationsPath: string;
  colorScheme?: string;
  fields: Array<{
    key: string;
    label: LocalizedString;
  }>;
  numberPrecision?: number;
  showOtherField?: boolean;
  forceFullAllocation?: boolean;
};

export type AllocationAnalysis = BaseDAOConfig & {
  type: DAOType.AllocationAnalysis;
  fields: Array<{
    key: string;
    label: LocalizedString;
    className: string;
    dataPaths: {
      path: string;
      selection?: number;
    }[];
    cellType: 'IMAGE' | 'HTML' | 'TEXT' | 'PERCENTAGE';
  }>;
};

export type AssetAllocationConfig = BaseDAOConfig & {
  type: DAOType.AssetAllocation;
  groupByAllocationsDAOOptions?: GroupByAllocationsDAOOptions;
  extraColumnsDAOOptions?: ExtraColumnsDAOOptions;
  showRowHeaderColors?: boolean;
  labelOverride?: Dictionary<LocalizedString>;
};

export type AggregatedAssetAllocationConfig = BaseDAOConfig & {
  type: DAOType.AggregatedAssetAllocation;
  view: CLIENT_LEVEL_VIEW;
  aggregatedPortfolioName?: string;
  groupByAllocationsDAOOptions?: GroupByAllocationsDAOOptions;
  extraColumnsDAOOptions?: ExtraColumnsDAOOptions;
  showRowHeaderColors?: boolean;
  labelOverride?: Dictionary<LocalizedString>;
};

export type ESGOverviewConfig = BaseDAOConfig & {
  type: DAOType.ESGOverview;
  groupByESGOverviewDAOOptions?: GroupByESGOverviewDAOOptions;
};

export type CountryAllocationConfig = BaseDAOConfig & {
  type: DAOType.CountryAllocation;
  filterTopXDAOOptions?: FilterTopXDAOOptions;
  extraColumnsDAOOptions?: ExtraColumnsDAOOptions;
};

export type CalendarYearReturnsConfig = BaseDAOConfig & {
  type: DAOType.CalendarYearReturns;
  filterTopXDAOOptions?: FilterTopXDAOOptions;
};

export type IndustryHoldingsConfig = BaseDAOConfig & {
  type: DAOType.IndustryHoldings;
  filterTopXDAOOptions?: FilterTopXDAOOptions;
};

export type TextConfig = BaseDAOConfig & {
  type: DAOType.Text;
  localizedTextDAOOptions: LocalizedTextDAOOptions;
};

export type GlossaryConfig = BaseDAOConfig & {
  type: DAOType.Glossary;
  glossaryConfigDAOOptions: GlossaryConfigDAOOptions;
};

export type HTMLContentConfig = BaseDAOConfig & {
  type: DAOType.HTMLContent;
  htmlContentDAOOptions: HTMLContentDAOOptions;
};

export type PortfolioValueBreakdownConfig = BaseDAOConfig & {
  type: DAOType.PortfolioValueBreakdown;
  view: CLIENT_LEVEL_VIEW;
  title: LocalizedString;
  className?: string;
};

export type ClientSummaryConfig = BaseDAOConfig & {
  type: DAOType.ClientSummary;
  sections?: unknown[]; // TODO: Define proper section config types
};

export type CommentaryConfig = BaseDAOConfig & {
  type: DAOType.Commentary;
  commentaryDAOOptions: CommentaryDAOOptions;
};

export type FixedIncomeRegionAllocationConfig = BaseDAOConfig & {
  type: DAOType.FixedIncomeRegionAllocation;
  regionExclusionConfigDAOOptions?: RegionExclusionConfigDAOOptions;
  rollIntoOther?: boolean;
};

export type FixedIncomeSectorAllocationConfig = BaseDAOConfig & {
  type: DAOType.FixedIncomeSectorAllocation;
  groupByFixedIncomeSectorGroupDAOOptions?: boolean;
  orderFixedIncomeSectorsByValueDAOOptions?: boolean;
  filterTopXDAOOptions?: FilterTopXDAOOptions;
  showEmptySectors?: boolean;
};

export enum FixedIncomeMetrics {
  'ALLOCATION' = 'ALLOCATION',
  'AVERAGE_COUPON' = 'AVERAGE_COUPON',
  'YIELD_TO_MATURITY' = 'YIELD_TO_MATURITY',
  'MODIFIED_DURATION' = 'MODIFIED_DURATION',
  'AVERAGE_QUALITY' = 'AVERAGE_QUALITY',
  'FACE_VALUE' = 'FACE_VALUE',
  'MATURITY_DATE' = 'MATURITY_DATE',
  'COUPON_DATES' = 'COUPON_DATES',
  'EFFECTIVE_DURATION' = 'EFFECTIVE_DURATION',
  'EFFECTIVE_MATURITY' = 'EFFECTIVE_MATURITY',
}

export type FixedIncomeMetricsConfig = BaseDAOConfig & {
  type: DAOType.FixedIncomeMetrics;
  metrics: Array<FixedIncomeMetrics>;
  labelOverride?: Dictionary<LocalizedString>;
};

export type FixedIncomeStyleConfig = BaseDAOConfig & {
  type: DAOType.FixedIncomeStyle;
};

export type FixedIncomeMaturityConfig = BaseDAOConfig & {
  type: DAOType.FixedIncomeMaturity;
  rollIntoOther?: boolean;
};

export type DrawdownAnalysisConfig = BaseDAOConfig & {
  type: DAOType.DrawdownAnalysis;
};

export type DrawdownDetailsConfig = BaseDAOConfig & {
  type: DAOType.DrawdownDetails;
  changeDateFormat?: string;
  labelOverride?: Dictionary<LocalizedString>;
};

export type LipperLeaderScoresConfig = BaseDAOConfig & {
  type: DAOType.LipperLeaderScores;
};

export type RiskRewardConfig = BaseDAOConfig & {
  type: DAOType.RiskReward;
};

export type CorrelationMatrixConfig = BaseDAOConfig & {
  type: DAOType.CorrelationMatrix;
};

export type CoreEquityMetricsConfig = BaseDAOConfig & {
  type: DAOType.CoreEquityMetrics;
  labelOverride?: Dictionary<LocalizedString>;
};

export type EquityStyleAllocationConfig = BaseDAOConfig & {
  type: DAOType.EquityStyleAllocation;
  displayCategories?: boolean;
  displayCapitalization?: boolean;
  displayOther?: boolean;
  displayNA?: boolean;
  labelOverride?: Dictionary<LocalizedString>;
};

export type EquityRegionAllocationConfig = BaseDAOConfig & {
  type: DAOType.EquityRegionAllocation;
  regionExclusionConfigDAOOptions?: RegionExclusionConfigDAOOptions;
};

export type SectorAllocationConfig = BaseDAOConfig & {
  type: DAOType.SectorAllocation;
  filterTopXDAOOptions?: FilterTopXDAOOptions;
  extraColumnsDAOOptions?: ExtraColumnsDAOOptions;
  displayOther?: boolean;
};

export type EquityCountryAllocationConfig = BaseDAOConfig & {
  type: DAOType.EquityCountryAllocation;
  countryInclusionConfigDAOOptions?: CountryInclusionConfigDAOOptions;
  displayOther?: boolean;
};

export type CreditQualityConfig = BaseDAOConfig & {
  type: DAOType.CreditQuality;
  groupBelowB?: boolean;
  rollIntoNotRated?: boolean;
};

export type UpDownConfig = BaseDAOConfig & {
  type: DAOType.UpDown;
  period: RiskPeriods;
};

export type BestWorstConfig = BaseDAOConfig & {
  type: DAOType.BestWorst;
  period: TimePeriods;
  interval: TimePeriods;
};

export type HypotheticalReturnConfig = BaseDAOConfig & {
  type: DAOType.HypotheticalReturn;
  colorOverride?: string;
  areaFillCondition?: AreaFillConditions[];
  highlightPreInceptionData?: boolean;
};

export type RiskMetricsConfig = BaseDAOConfig & {
  type: DAOType.RiskMetrics;
  showAdvanced: boolean;
  showInformationRatio: boolean;
  showAlpha?: boolean;
  showCore?: boolean;
  showRiskRating?: boolean;
  growthPeriods: Array<GrowthTypes>;
  labelOverride?: Dictionary<LocalizedString>;
  showAsPercentage?: Dictionary<LocalizedString>;
};

export type DetailedHoldingsConfig = BaseDAOConfig & {
  type: DAOType.DetailedHoldings;
  filterTopXDAOOptions?: FilterTopXDAOOptions;
  extraColumnsDAOOptions?: ExtraColumnsDAOOptions;
  showSubtotal?: boolean;
};

export type PortfolioHoldingsConfig = BaseDAOConfig & {
  type: DAOType.PortfolioHoldings;
  filterTopXDAOOptions?: FilterTopXDAOOptions;
  groupByAssetType?: boolean;
  filterHoldingType?: Array<'fund' | 'stock' | 'benchmark'>;
  extraColumnsDAOOptions?: ExtraColumnsDAOOptions;
  fieldAsterisks?: string[];
  showAssetGroupTotals?: boolean;
  showAllPortfolioTotals?: boolean;
  usePortfolioTotalFee?: boolean;
  growthPeriods: Array<GrowthTypes>;
  showMER?: boolean;
  labelOverride?: Dictionary<LocalizedString>;
};

export type PortfolioFundHoldingsConfig = BaseDAOConfig & {
  type: DAOType.PortfolioFundHoldings;
  filterTopXDAOOptions?: FilterTopXDAOOptions;
  groupByAssetType?: boolean;
  filterHoldingType?: Array<'fund' | 'stock' | 'benchmark'>;
  extraColumnsDAOOptions?: ExtraColumnsDAOOptions;
};

export type PortfolioYieldConfig = BaseDAOConfig & {
  type: DAOType.PortfolioHoldings;
  groupByAssetType?: boolean;
  extraColumnsDAOOptions?: ExtraColumnsDAOOptions;
  fieldAsterisks?: string[];
  totalYieldTotals?: boolean;
  totalYieldSubTotals?: boolean;
};

export type BmoGifSummaryConfig = BaseDAOConfig & {
  type: DAOType.BmoGifSummary;
  section: 'PERSONAL_DATA' | 'INITIAL_DEPOSIT' | 'INVESTMENT_ASSUMPTIONS' | 'ALL';
};

export type BmoGifConfig = BaseDAOConfig & {
  type: DAOType.BmoGif;
  pageIndex: number;
  isGraph: boolean;
  isScenarioSummary: boolean;
};

export type DisclaimrsConfig = BaseDAOConfig & {
  type: DAOType.Disclaimers;
};

export type IncomeStreamGraphConfig = BaseDAOConfig & {
  type: DAOType.IncomeStreamGraph;
};

export type IncomeStreamSummaryConfig = BaseDAOConfig & {
  type: DAOType.IncomeStreamSummary;
};

export type IncomeStreamConfig = BaseDAOConfig & {
  type: DAOType.IncomeStream;
  extraColumnsDAOOptions?: ExtraColumnsDAOOptions;
  distributionTable: boolean;
};

export type RiskProfileConfig = BaseDAOConfig & {
  type: DAOType.RiskProfile;
  ruleIndex: number;
  summaryView?: boolean;
  displayTarget?: boolean;
  borderWidth?: number;
  showTarget?: boolean;
  sizes: {
    actual: {
      inner?: number;
      outer?: number;
    };
    target: {
      inner?: number;
      outer?: number;
    };
  };
};

export type RebalanceAnalysisConfig = BaseDAOConfig & {
  type: DAOType.RebalanceAnalysis;
  showRebalancedHoldings?: boolean;
  showDivestedHoldings?: boolean;

  // The follow properties is generated at runtime - not expected to come directly from the template.
  name?: LocalizedString;
  holdings?: RebalancedHoldingData[];
};

export type TaxAnalysisOverviewConfig = BaseDAOConfig & {
  type: DAOType.TaxAnalysisOverview;
};

export type TaxAnalysisTransitionImplicationsConfig = BaseDAOConfig & {
  type: DAOType.TaxAnalysisTransitionImplications;
};

export type TaxAnalysisPortfolioChangeLogConfig = BaseDAOConfig & {
  type: DAOType.TaxAnalysisPortfolioChangeLog;
};

export type ESGCarbonIntensityConfig = BaseDAOConfig & {
  type: DAOType.ESGCarbonIntensity;
};

export type ESGImpliedTemperatureRiseConfig = BaseDAOConfig & {
  type: DAOType.ESGImpliedTemperatureRise;
};

// VIEW OPTIONS

export type DataViewOptions =
  | TextViewOptions
  | HtmlViewOptions
  | TextAreaViewOptions
  | GridViewOptions
  | TableViewOptions
  | DonutViewOptions
  | LineViewOptions
  | ScatterViewOptions
  | BarViewOptions
  | ChartToolbarOptions
  | ViewControlOptions;

export type TextViewOptions = {
  type: DataView.Text;
  className?: string;
};

export type HtmlViewOptions = {
  type: DataView.Html;
  className?: string;
  accessibilityDescription?: LocalizedString;
};

export type TextAreaViewOptions = {
  type: DataView.TextArea;
  className?: string;
};

export type TableViewOptions = {
  type: DataView.Table;
  showHeader?: boolean;
  maxRowsPerPage?: number;
  dynamicMaxRowsPerPage?: {
    Portrait: number;
    Landscape: number;
  };
  className?: string;
  accessibilityDescription?: LocalizedString;
};

export type GridViewOptions = {
  type: DataView.Grid;
  columns?: number;
  className?: string;
};

export type DonutViewOptions = {
  className?: string;
  type: DataView.Donut;
  showHeader: boolean;
  showLegend: boolean;
  showTotal: boolean;
  configOptions: DonutChartConfigOptions;
  accessibilityDescription?: LocalizedString;
};

export type LineViewOptions = {
  type: DataView.Line;
  configOptions: LineChartConfigOptions;
  className?: string;
  accessibilityDescription?: LocalizedString;
};

export type ScatterViewOptions = {
  type: DataView.Scatter;
  configOptions: ScatterPlotConfigOptions;
  accessibilityDescription?: LocalizedString;
};

export type BarViewOptions = {
  type: DataView.Bars;
  valueFormatter?: ValueFormatter;
  className?: string;
  configOptions: BarChartConfigOptions;
  showHeader?: boolean;
  accessibilityDescription?: LocalizedString;
};

export type ChartToolbarOptions = {
  type: DataView.ChartToolbar;
  className?: string;
};

export type ViewControlOptions = {
  type: DataView.ViewControl;
  key: string;
  availableViews: string[];
  className?: string;
};

export enum ToolbarElement {
  GrowthOfCapital = 'GROWTH_OF_CAPITAL',
  RateOfReturn = 'RATE_OF_RETURN',
  DateQuickRange = 'DATE_QUICK_RANGE',
  ShortHistory = 'SHORT_HISTORY',
  DateRangeSelect = 'DATE_RANGE_SELECT',
  RebalanceFrequency = 'REBALANCE_FREQUENCY',
  UseMonthlyData = 'USE_MONTHLY_DATA',
}

export enum DataView {
  Text = 'TEXT',
  Html = 'HTML',
  Table = 'TABLE',
  Grid = 'GRID',
  Donut = 'DONUT',
  Line = 'LINE',
  Scatter = 'SCATTER',
  Bars = 'BARS',
  ChartToolbar = 'CHART_TOOLBAR',
  ViewControl = 'VIEW_CONTROL',
  TextArea = 'TEXTAREA',
}

// CHART OPTIONS
export enum ChartLabelFormatterType {
  Dollars = 'DOLLARS',
  DollarsNoSuffix = 'DOLLARS_NO_SUFFIX',
  Date = 'DATE',
  Decimal = 'DECIMAL',
}

export type ChartConfigOptions = {
  title?: {
    text: string;
    useHTML?: boolean;
    floating?: boolean;
    x?: number;
    y?: number;
  };
  chart?: {
    backgroundColor?: string;
    height?: number;
    inverted?: boolean;
    width?: number;
    className?: string;
    margin?: [number, number, number, number];
  };
  xAxis?: {
    plotBands: XAxisPlotBandsOptions[];
    plotLines: XAxisPlotLinesOptions[];
    opposite?: boolean;
    type?: xAxisType;
    min?: number;
    title?: {
      text?: LocalizedString;
    };
    crosshair?: {
      width: number;
    };
    gridLineWidth?: number;
    labels?: {
      rotation?: number;
      format?: string;
      formatter?: FormatterCallbackFunction<AxisLabelsFormatterContextObject>;
      formatterType?: string;
      style?: {
        fontSize: string;
      };
    };
    lineWidth?: number;
    lineColor?: string;
    reversed?: boolean;
  };
  yAxis?: {
    showLastLabel?: boolean;
    maxPadding?: number;
    type?: yAxisType;
    min?: number;
    title?: {
      text: LocalizedString;
    };
    stackLabels?: {
      enabled: boolean;
    };
    gridLineWidth?: number;
    labels?: {
      x?: number;
      y?: number;
      formatterType?: ChartLabelFormatterType;
      rotation?: number;
      format?: string;
      decimals?: number;
      style?: {
        fontSize?: string;
      };
    };
    opposite?: boolean;
    lineWidth?: number;
    lineColor?: string;
    startOnTick?: boolean;
    tickPixelInterval?: number;
    tickInterval?: number;
  };
  plotOptions?: {
    series?: SeriesOptions;
    [key: string]: PlotOptions | undefined;
  };
  series?: SeriesSpecificOptions[];
  legend?: {
    enabled?: boolean;
  };
  tooltip?: {
    enabled?: boolean;
  };
};

export type xAxisType = 'linear' | 'logarithmic' | 'datetime' | 'category';

export type yAxisType = 'linear' | 'logarithmic' | 'datetime' | 'category' | 'treegrid';

export type PlotOptions = {
  borderWidth?: number;
  shadow?: boolean;
  center?: string[];
  innerSize?: string | number;
  size?: string | number;
  pointWidth?: number;
  pointPadding?: number;
  groupPadding?: number;
  dataLabels?: {
    enabled?: boolean;
    format?: string;
  };
  states?: {
    hover?: {
      lineWidth?: number;
      radiusPlus?: number;
      enabled?: boolean;
    };
    inactive?: {
      opacity?: number;
    };
  };
  animation?: boolean;
  getExtremesFromAll?: boolean;
};

export type SeriesOptions = {
  enableMouseTracking?: boolean;
  animation?: boolean;
  label?: { enabled?: boolean };
  dataLabels?: {
    enabled?: boolean;
    format?: string;
  };
};

export type SeriesSpecificOptions = PieSeriesOptions;

export type PieSeriesOptions = {
  type: 'pie';
  size: string | number;
  innerSize: string | number;
};

export type DonutChartConfigOptions = ChartConfigOptions;

export type BarChartConfigOptions = ChartConfigOptions;

export type ScatterPlotConfigOptions = ChartConfigOptions;

export type LineChartConfigOptions = ChartConfigOptions;

export type ProposalTheme = {
  key: string;
  useCSSBannerStyles: boolean;
  logos?: {
    header?: LocalizedString;
  };
};

export enum TEXT_AREA_KEYS {
  COMMENTARY = 'COMMENTARY',
  ABOUT = 'ABOUT',
}

export type ProposalSectionPageMapping = {
  [key: string]: { start: number; end: number | null };
};

export type TableOfContentsData = {
  tableOfContentsGroup: TableOfContentsGroupData;
  startsAtPageIndex: number;
  pages: number;
};

export type TableOfContentsGroupData = {
  key: string;
  label: LocalizedString;
};
